import { VoucherProviderCreateForm } from '@/components/voucher-providers/VoucherProviderCreateForm';
import { ImageUrlsField } from '@/components/ui/image-urls-field';
import type { VoucherProviderFormData } from '@/components/voucher-providers/VoucherProviderForm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, Eye } from 'lucide-react';
import React, { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Link } from 'react-router';
import { Form } from '@/components/ui/form';
import { ProviderCategory } from '@/api/model';

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getCategoryLabel = (category: ProviderCategory): string => {
  const labels: Record<ProviderCategory, string> = {
    [ProviderCategory.GASTRONOMIE]: 'Gastronomie',
    [ProviderCategory.ERLEBEN]: 'Erleben',
    [ProviderCategory.SHOPPING]: 'Shopping',
    [ProviderCategory.SERVICES]: 'Services',
  };
  return labels[category] || category;
};

export const VoucherProviderCreatePage: React.FC = () => {
  const [previewData, setPreviewData] = useState<VoucherProviderFormData>({
    name: '',
    email: '',
    address: '',
    phone: '',
    short_description: '',
    logo_urls: [],
    category: ProviderCategory.SERVICES,
  });
  const [formInstance, setFormInstance] = useState<UseFormReturn<VoucherProviderFormData> | null>(null);

  return (
    <div className='space-y-6'>
      {/* Page Header */}
      <div className='flex flex-col gap-2'>
        <div className='flex items-center gap-4'>
          <Link to='/admin/providers'>
            <ArrowLeft className='h-6 w-6 text-swd-tuerkis-dark' />
          </Link>

          <h1 className='text-3xl leading-9 font-semibold text-swd-tuerkis-darker'>Neuen Gutscheinpartner erstellen</h1>
        </div>
        <p className='text-base leading-6 font-medium tracking-wide text-neutral-grau'>
          Legen Sie einen neuen Partner an. Partner können anschließend Gutscheine erstellen und verwalten.
        </p>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Form Section */}
        <div className='lg:col-span-2'>
          <VoucherProviderCreateForm onFormDataChange={setPreviewData} hideLogoField onFormInstance={setFormInstance} />
        </div>

        {/* Preview Section */}
        <div className='lg:sticky lg:top-6 lg:self-start lg:col-span-1 space-y-6'>
          <Card className='border border-neutral-hellgrau rounded-lg'>
            <CardHeader>
              <div className='flex items-center gap-2'>
                <Eye className='h-5 w-5 text-swd-tuerkis-darker' />
                <CardTitle className='text-neutral-dunkelgrau text-xl font-bold leading-7'>Vorschau</CardTitle>
              </div>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='flex flex-col items-center gap-4'>
                <Avatar className='h-24 w-24'>
                  <AvatarImage
                    src={
                      previewData.logo_urls && previewData.logo_urls.length > 0 ? previewData.logo_urls[0] : undefined
                    }
                    alt={previewData.name || 'Partner'}
                  />
                  <AvatarFallback className='text-2xl font-semibold'>
                    {getInitials(previewData.name || 'Partner')}
                  </AvatarFallback>
                </Avatar>
                <div className='text-center'>
                  <h3 className='font-semibold text-lg'>{previewData.name || 'Partnername'}</h3>
                  <p className='text-sm text-neutral-mittelgrau'>{getCategoryLabel(previewData.category)}</p>
                  {previewData.short_description && (
                    <p className='text-sm text-neutral-grau mt-2'>{previewData.short_description}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {formInstance && (
            <Form {...formInstance}>
              <ImageUrlsField control={formInstance.control} name='logo_urls' label='Logo' maxImages={1} />
            </Form>
          )}
        </div>
      </div>
    </div>
  );
};
